import { useEffect, useRef, useState } from 'react';
import inquiryService, { shouldSkipImmediateUnreadCountFetch } from '../services/inquiryService';

/**
 * Custom Hook fuer das Polling der ungelesenen Anfragen-Anzahl.
 *
 * Verhalten:
 * - Kein Polling wenn `user` falsy (z.B. nicht eingeloggt) oder `enabled` false.
 * - Erster Fetch wird uebersprungen wenn shouldSkipImmediateUnreadCountFetch()
 *   true zurueckgibt (d.h. letzter Fetch war vor weniger als 30 Sekunden).
 *   Verhindert doppelte Backend-Calls bei schnellem Remount / Routenwechsel.
 *   AUSNAHME: Bei Tab-Rueckkehr (visibilitychange) wird der Check bewusst
 *   uebersprungen, weil der Nutzer sofort aktuelle Daten erwartet.
 * - Interval-Tick alle 60 Sekunden.
 * - In-Flight-Guard verhindert parallele Requests: ein neuer Fetch startet nur,
 *   wenn der vorherige abgeschlossen ist (isFetchingRef).
 * - Visibility-Handling: Interval wird pausiert wenn Tab versteckt wird und
 *   beim Wiedersichtbarwerden sofort neu gestartet (mit sofortigem Fetch).
 * - BFCache-Handling: pageshow-Event (event.persisted) behandelt Vor-/Zurueck-
 *   Navigation in Browsern, die kein visibilitychange feuern (z.B. Safari iOS).
 * - Initialer Fetch und Interval-Start werden uebersprungen wenn der Tab beim
 *   Mount bereits versteckt ist — der visibilitychange-Handler uebernimmt dann.
 * - Cleanup bei Unmount: Interval stoppen, Event-Listener entfernen.
 * - 401-Fehler werden global vom apiClient-Interceptor behandelt (Logout+Redirect).
 *   Alle anderen Fehler werden still ignoriert; der Badge bleibt am alten Wert.
 *
 * @param {Object|null|undefined} user - Aktuell eingeloggter Benutzer aus AuthContext
 * @param {boolean} [enabled=true] - Wenn false, wird kein Polling gestartet (z.B. fuer RENTER)
 * @returns {number} Anzahl ungelesener Anfragen (0 wenn kein user oder disabled)
 */
export default function useInquiryUnreadCountPolling(user, enabled = true) {
  const [count, setCount] = useState(0);
  const intervalRef = useRef(null);
  const isFetchingRef = useRef(false);

  // Dependency auf user?.id statt user-Objekt — verhindert unnoetige
  // Restarts wenn AuthContext ein neues Objekt-Identity bei Re-Renders erzeugt.
  const isAuthenticated = Boolean(user?.id);

  useEffect(() => {
    if (!isAuthenticated || !enabled) {
      setCount(0);
      return;
    }

    let cancelled = false;

    const fetchCount = async () => {
      // In-Flight-Guard — kein paralleler Request wenn noch einer laeuft.
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      try {
        const result = await inquiryService.getUnreadCount();
        if (!cancelled) {
          setCount(result ?? 0);
        }
      } catch {
        // 401 wird global vom apiClient-Interceptor behandelt (Logout+Redirect).
        // Alle anderen Fehler still ignorieren — Badge bleibt am alten Wert.
      } finally {
        isFetchingRef.current = false;
      }
    };

    const startInterval = () => {
      if (intervalRef.current !== null) return;
      intervalRef.current = setInterval(fetchCount, 60_000);
    };

    const stopInterval = () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopInterval();
      } else {
        // Bewusst KEIN shouldSkipImmediateUnreadCountFetch-Check:
        // Bei Tab-Rueckkehr will der Nutzer sofort aktuelle Daten sehen.
        fetchCount();
        startInterval();
      }
    };

    // BFCache-Handling fuer Vor-/Zurueck-Navigation (insbesondere Safari iOS).
    // In diesen Browsern feuert kein visibilitychange, sondern nur pageshow mit
    // event.persisted === true. Ohne diesen Handler wuerde der Badge stale bleiben.
    const handlePageShow = (event) => {
      if (event.persisted && !document.hidden && intervalRef.current === null) {
        fetchCount();
        startInterval();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);

    // Initialer Fetch und Interval nur starten wenn Tab bereits sichtbar.
    // Wenn Tab beim Mount versteckt ist (Hintergrund-Tab, PWA-Wakeup): nichts tun —
    // der visibilitychange-Handler uebernimmt, sobald der Tab sichtbar wird.
    if (!document.hidden) {
      if (!shouldSkipImmediateUnreadCountFetch()) {
        fetchCount();
      }
      startInterval();
    }

    return () => {
      cancelled = true;
      stopInterval();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [isAuthenticated, enabled]);

  return count;
}
